import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSelector } from "react-redux";
import useApiCall from "../../hooks/useApiCall";
import useToast from "../../hooks/useToast";
import { INDIGO, SLATE } from "../../constants/Colors";
import { AGENT_TYPES } from "../../constants/AgentTypes";
import { MODEL_OPTIONS, MODEL_LABEL_BY_VALUE } from "../../constants/ModelOptions";

const INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontSize: "0.875rem",
    bgcolor: "#ffffff",
    "& fieldset": { borderColor: SLATE[300] },
    "&:hover fieldset": { borderColor: SLATE[400] },
    "&.Mui-focused fieldset": { borderColor: INDIGO[500] },
  },
  "& .MuiInputBase-input": { color: SLATE[900] },
};

const FIELD_LABEL_SX = {
  fontSize: "0.8125rem",
  fontWeight: 500,
  color: SLATE[700],
  mb: "6px",
  display: "block",
};

/**
 * Counts the sentences the server will actually test. Mirrors
 * IntentModelTestRunStore.parse_sentences on the API side -- blanks, # comments and
 * duplicates are dropped -- so the number shown here is the number that gets run.
 */
export function countSentences(raw) {
  const seen = new Set();
  (raw || "").split(/\r?\n/).forEach((line) => {
    const trimmed = line.trim();
    if (trimmed && !trimmed.startsWith("#")) seen.add(trimmed);
  });
  return seen.size;
}

export default function NewTestRunDialog({ open, onClose, onCreated, tokens }) {
  const apps = useSelector((state) => state?.apps.list || []);
  const { makeRequest } = useApiCall();
  const toast = useToast();

  const [market, setMarket] = useState("");
  const [agentType, setAgentType] = useState("");
  const [model, setModel] = useState("");
  const [fallbackModel, setFallbackModel] = useState("");
  const [sentences, setSentences] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const marketItems = useMemo(
    () => apps.map((app) => ({ value: app.market, label: app.market })),
    [apps]
  );

  const sentenceCount = useMemo(() => countSentences(sentences), [sentences]);
  const canSubmit = Boolean(market) && Boolean(agentType) && sentenceCount > 0 && !submitting;

  useEffect(() => {
    if (open) return;
    setMarket("");
    setAgentType("");
    setModel("");
    setFallbackModel("");
    setSentences("");
    setError(null);
    setSubmitting(false);
  }, [open]);

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);
    try {
      await makeRequest("/v1/intent_model_test_runs", {
        method: "POST",
        body: {
          market,
          agent_type: agentType,
          sentences,
          model: model.trim() || undefined,
          fallback_model: fallbackModel.trim() || undefined,
        },
        authTokens: tokens,
      });
      toast.success(
        `Queued ${sentenceCount} ${sentenceCount === 1 ? "sentence" : "sentences"} for comparison on ${market}`
      );
      if (onCreated) onCreated();
      onClose();
    } catch (e) {
      setError(e?.message || "Failed to start the test run.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: "88vh", maxHeight: "88vh", width: "90vw", maxWidth: "900px" } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Typography sx={{ fontSize: "1rem", fontWeight: 600, color: SLATE[900] }}>
          New Test
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: SLATE[500] }}>
          Every sentence runs through both the LLM and the legacy intent model.
        </Typography>
      </DialogTitle>

      <DialogContent
        sx={{ display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}
      >
        {error && (
          <Alert severity="error" sx={{ fontSize: "0.8125rem" }}>
            {error}
          </Alert>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography component="label" htmlFor="test-run-market" sx={FIELD_LABEL_SX}>
              Market
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              id="test-run-market"
              label="Market"
              value={market}
              onChange={(e) => setMarket(e.target.value)}
              sx={INPUT_SX}
            >
              {marketItems.map((m) => (
                <MenuItem key={m.value} value={m.value}>
                  {m.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography component="label" htmlFor="test-run-type" sx={FIELD_LABEL_SX}>
              Type
            </Typography>
            <TextField
              select
              fullWidth
              size="small"
              id="test-run-type"
              label="Type"
              value={agentType}
              onChange={(e) => setAgentType(e.target.value)}
              sx={INPUT_SX}
            >
              {AGENT_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>
                  {t.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <Box sx={{ flex: 1 }}>
            <Typography component="label" htmlFor="test-run-model" sx={FIELD_LABEL_SX}>
              Model
            </Typography>
            <Autocomplete
              freeSolo
              size="small"
              fullWidth
              id="test-run-model"
              options={MODEL_OPTIONS.map((m) => m.value)}
              inputValue={model}
              onInputChange={(_, newInputValue) => setModel(newInputValue)}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", color: SLATE[900] }}>{MODEL_LABEL_BY_VALUE[option]}</Typography>
                    <Typography sx={{ fontSize: "0.6875rem", color: SLATE[400] }}>{option}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="Agent default" sx={INPUT_SX} />
              )}
            />
          </Box>

          <Box sx={{ flex: 1 }}>
            <Typography component="label" htmlFor="test-run-fallback-model" sx={FIELD_LABEL_SX}>
              Fallback Model
            </Typography>
            <Autocomplete
              freeSolo
              size="small"
              fullWidth
              id="test-run-fallback-model"
              options={MODEL_OPTIONS.map((m) => m.value)}
              inputValue={fallbackModel}
              onInputChange={(_, newInputValue) => setFallbackModel(newInputValue)}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  <Box>
                    <Typography sx={{ fontSize: "0.875rem", color: SLATE[900] }}>{MODEL_LABEL_BY_VALUE[option]}</Typography>
                    <Typography sx={{ fontSize: "0.6875rem", color: SLATE[400] }}>{option}</Typography>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} placeholder="Agent default" sx={INPUT_SX} />
              )}
            />
          </Box>
        </Stack>
        <Typography sx={{ fontSize: "0.75rem", color: SLATE[500], mt: "-8px" }}>
          Leave blank to use the agent's default model.
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
          <Typography component="label" htmlFor="test-run-sentences" sx={FIELD_LABEL_SX}>
            Sentences
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={20}
            maxRows={30}
            id="test-run-sentences"
            label="Sentences"
            placeholder={"Paste one sentence per line.\nBlank lines, duplicates and # comments are ignored."}
            value={sentences}
            onChange={(e) => setSentences(e.target.value)}
            sx={{
              ...INPUT_SX,
              "& .MuiInputBase-input": { fontSize: "0.8125rem", lineHeight: 1.7 },
            }}
          />
          <Typography sx={{ fontSize: "0.75rem", color: SLATE[500], mt: "8px" }}>
            {sentenceCount} {sentenceCount === 1 ? "sentence" : "sentences"} will be tested against both models
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          sx={{ textTransform: "none", fontSize: "0.875rem", color: SLATE[600] }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          sx={{ textTransform: "none", fontSize: "0.875rem", fontWeight: 500, boxShadow: "none" }}
        >
          {submitting && <CircularProgress size={14} sx={{ color: "#fff", mr: 1 }} />}
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
